import formatFilterState, { convertToFilterObj } from "@Pages/Search/utils/formatFilterState";
import { AppThunk } from "@Store/index";
import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import fetchInventoryRooms from "./fetchInventoryRooms";
import getPropertyPageData from "./getPropertyPageData";
import { getFiltersFromQuery, getParams, syncQueryWithURL } from "./helper";
import { filterActiveChildInventories } from "@Pages/Search/components/RecentelyViewedSection/helper";
import urlBuilder from "@Utils/urlBuilder";
import { isDemoWhitelabelSubdomain } from "constants/index";
import getCookie from "helpers/getCookie";
import config from "@Config/index";

interface PropertyPageData {
  readyStatus: "invalid" | "request" | "success" | "failure";
  canonical_name: string;
  roomTypes: {
    isLoading: boolean;
    count: number;
    data: Array<any>;
    isLoadingForFilter: boolean;
    id: string;
    showPrevDataWithoutShimmer: boolean;
  };
  data: any;
  id: number | null;
  error: any;
  reviewData: any;
  inventoryReviewData: any;
  filters: any;
  gallery_modal: {
    show: boolean;
    selectedTab: string;
    data: any;
  };
  gallery_carousel: {
    fromGallery: any;
    show: boolean;
    images: any;
    category: string;
    currentItemIndex: number;
    showViewRoomButton: boolean;
    reviewSection: boolean;
    config_id: string;
  };
  video_modal: {
    show: boolean;
    videoId: string;
  };
  review_media_gallery_open: boolean;
  regionId: string;
  showShareModal: boolean;
  shareUrl: string;
  showNextRoomButton: boolean;
  nextRoomId: string;
  expandedRoomType: number | null;
  href?: string;
  nearbyData: {
    cities: Array<any>;
    areas: Array<any>;
    universities: Array<any>;
    inventories: Array<any>;
  };
  nearby_properties: Array<any>;
  nearbyLocations: Array<any> | null;
  installment_plans: Array<any> | null;
  installment_plans_fetching: boolean;
  budgetFriendlyProperties: Array<any> | null;
  priceTrendsData: any | null;
  propertyFilterOptions: any | null;
}

export const initialState: PropertyPageData = {
  readyStatus: "invalid",
  canonical_name: "",
  error: "",
  roomTypes: {
    isLoading: false,
    count: 0,
    data: [],
    isLoadingForFilter: false,
    id: "",
    showPrevDataWithoutShimmer: false,
  },
  data: null,
  id: null,
  reviewData: null,
  inventoryReviewData: null,
  filters: {},
  gallery_modal: {
    show: false,
    selectedTab: "all",
    data: {},
  },
  gallery_carousel: {
    show: false,
    images: [],
    category: "",
    currentItemIndex: 0,
    showViewRoomButton: true,
    reviewSection: false,
    config_id: "",
    fromGallery: false,
  },
  video_modal: {
    show: false,
    videoId: "",
  },
  review_media_gallery_open: false,
  regionId: "",
  showShareModal: false,
  shareUrl: "",
  showNextRoomButton: false,
  nextRoomId: "",
  expandedRoomType: null,
  href: "",
  nearbyData: {
    cities: [],
    areas: [],
    universities: [],
    inventories: [],
  },
  nearby_properties: [],
  nearbyLocations: null,
  installment_plans: null,
  installment_plans_fetching: false,
  budgetFriendlyProperties: null,
  priceTrendsData: null,
  propertyFilterOptions: null,
};

const propertyPageData = createSlice({
  name: "propertyPageData",
  initialState,
  reducers: {
    getPropertyPageRequesting: (state: PropertyPageData, { payload }: PayloadAction<any>) => {
      state.canonical_name = payload.id;
      state.readyStatus = "request";
      state.roomTypes.isLoading = true;
      state.filters = initialState.filters;
      state.regionId = payload.regionId;
      state.installment_plans = null;
      state.installment_plans_fetching = false;
      if (__CLIENT__) window.scrollTo(0, 0);
    },
    getPropertyPageSuccess: (state: PropertyPageData, { payload }: PayloadAction<any>) => {
      state.data = payload?.data;
      state.id = payload.data.id;
      state.reviewData = payload?.reviewData;
      state.filters = payload?.filters || {};
      state.readyStatus = "success";
    },
    getPropertyPageError: (state: PropertyPageData, { payload }: PayloadAction<any>) => {
      state.error = payload;
      state.readyStatus = "failure";
      state.roomTypes.isLoading = false;
    },
    getGalleryModalData: (state: PropertyPageData, { payload }: PayloadAction<any>) => {
      state.gallery_modal = payload;
    },
    updateBasicDetails: (state: PropertyPageData, { payload }: PayloadAction<any>) => {
      if (state.data?.id !== payload?.id) {
        state.data = { ...state.data, ...payload };
        state.id = payload.id;
      }
    },
    getGalleryCarouselData: (state: PropertyPageData, { payload }: PayloadAction<any>) => {
      state.gallery_carousel = payload;
    },
    getVideoModalData: (state: PropertyPageData, { payload }: PayloadAction<any>) => {
      state.video_modal = payload;
    },
    setReviewMediaGalleryOpen: (state: PropertyPageData, { payload }: PayloadAction<boolean>) => {
      state.review_media_gallery_open = payload;
    },
    updatePropertyFilters: (state, { payload }: PayloadAction<any>) => {
      state.filters = payload || {};
    },
    updateRoomTypes: (state, { payload }: PayloadAction<any>) => {
      state.roomTypes.isLoading = payload?.isLoading;
      state.roomTypes.data = filterActiveChildInventories(payload?.data);
      state.roomTypes.count = payload?.count;
      state.roomTypes.isLoadingForFilter = payload?.isLoadingForFilter;
      state.roomTypes.id = payload?.id;
      state.roomTypes.showPrevDataWithoutShimmer = payload?.showPrevDataWithoutShimmer || false;
    },
    updateNearbyData: (state, { payload }: PayloadAction<any>) => {
      state.nearbyData = payload;
    },
    updateNearbyProperties: (state, { payload }: PayloadAction<any>) => {
      state.nearby_properties = payload;
    },
    updateNearbyLocations: (state, { payload }: PayloadAction<any>) => {
      state.nearbyLocations = payload;
    },
    setShowShareModalData: (state: PropertyPageData, { payload }: PayloadAction<any>) => {
      if (typeof payload === "boolean") {
        state.showShareModal = payload;
        state.shareUrl = "";
      } else {
        state.showShareModal = payload.show;
        state.shareUrl = payload.url || "";
      }
    },
    setShowNextRoomButton: (state: PropertyPageData, { payload }: PayloadAction<any>) => {
      state.showNextRoomButton = payload.state;
      state.nextRoomId = payload.id;
    },
    setExpandedRoomType: (state: PropertyPageData, { payload }: PayloadAction<any>) => {
      state.expandedRoomType = payload;
    },
    updatePropertyPageFilters: (state: PropertyPageData, { payload }: PayloadAction<any>) => {
      state.filters = payload?.filters || {};
    },
    updatePropertyPageHref: (state: PropertyPageData, { payload }: PayloadAction<any>) => {
      state.href = payload;
    },
    updateInstallmentPlans: (state: PropertyPageData, { payload }: PayloadAction<any>) => {
      state.installment_plans = payload.installment_plans;
      state.installment_plans_fetching = false;
    },
    setInstallmentPlansFetching: (state: PropertyPageData, { payload }: PayloadAction<boolean>) => {
      state.installment_plans_fetching = payload;
    },
    updateBudgetFriendlyProperties: (state: PropertyPageData, { payload }: PayloadAction<any>) => {
      state.budgetFriendlyProperties = payload;
    },
    updateInventoryReviewData: (state: PropertyPageData, { payload }: PayloadAction<any>) => {
      state.inventoryReviewData = payload;
    },
    updatePriceTrendsData: (state: PropertyPageData, { payload }: PayloadAction<any>) => {
      state.priceTrendsData = payload;
    },
    updatePropertyFilterOptions: (state: PropertyPageData, { payload }: PayloadAction<any>) => {
      state.propertyFilterOptions = payload;
    },
  },
});

export const applyPropertyFilter =
  (filters: any): AppThunk =>
  async (dispatch, getState) => {
    const state = getState();
    const filterObj = convertToFilterObj(filters);

    let newUrl = syncQueryWithURL(filterObj);
    if (
      state.whitelabelData.isWhitelabel &&
      isDemoWhitelabelSubdomain(state.whitelabelData.subdomain)
    ) {
      newUrl = urlBuilder(newUrl, {
        params: { partner_id: state.whitelabelData.partner_uuid },
      });
    }
    const { href } = window.location;
    const filterFromQuery = getFiltersFromQuery(newUrl);
    const filterParams = getParams(filterFromQuery);

    dispatch(updatePropertyFilters(filters));
    dispatch(updateRoomTypes({ isLoadingForFilter: true }));

    const res = await fetchInventoryRooms({
      id: state.propertyPageData.data?.id,
      params: filterParams,
      selectedLang: state?.appData?.userLanguage,
    });

    const data = res?.data?.data?.result || [];
    const count = res?.data?.data?.result?.length || 0;

    if (data && data?.active_children_with_filters?.length >= 0) {
      data.active_children = [...data?.active_children_with_filters];
    }
    dispatch(updateRoomTypes({ isLoadingForFilter: false, data, count }));

    window.history.replaceState(window.history.state, "", newUrl ?? href);
  };

export const shouldFetchProperty = (
  propertyPageDataState: PropertyPageData,
  canonical_name: string,
  regionId?: any,
  removeRegionCheck = false,
) =>
  propertyPageDataState.readyStatus === "invalid" ||
  propertyPageDataState.canonical_name !== canonical_name ||
  (!removeRegionCheck && propertyPageDataState.regionId !== regionId);

export const fetchPropertyPageDataIfNeeded =
  (props: any): AppThunk =>
  async (dispatch, getState: any) => {
    const canonical_name = __CLIENT__ ? props?.match?.params?.id : props?.params?.id;
    const href = (__CLIENT__ ? window.location.href : props.req?.url) || null;
    const state = getState();
    let regionId = "";
    let userCurrency = "";
    if (__CLIENT__) {
      regionId = getCookie("searchRegionId") || "";
      userCurrency = getCookie("user_currency") || "";
    } else {
      regionId = props.req.cookies?.searchRegionId;
      userCurrency = props.req.cookies?.user_currency;
    }
    if (!userCurrency && state.appData.userCurrency) {
      userCurrency = state.appData.userCurrency;
    }
    if (regionId === "undefined") {
      regionId = "";
    }
    let token = "";

    if (!__CLIENT__) {
      token = props.req.cookies.user_auth_token;
    }
    let selectedLang = state.appData.userLanguage;
    if (!__CLIENT__ && config.IS_CHINA_APP) {
      selectedLang = state.appData.userLanguage || "zh";
    }
    const filters = getFiltersFromQuery(href) || {};
    const filterParams = getParams(filters);
    // @ts-ignore
    const formattedFilters = formatFilterState(filterParams);

    dispatch(
      updatePropertyPageFilters({
        filters: formattedFilters,
      }),
    );

    if (shouldFetchProperty(getState().propertyPageData, canonical_name, regionId, !regionId)) {
      return dispatch(
        getPropertyPageData({
          id: props.canonical_name ?? canonical_name,
          url: props.href ?? href,
          staticContext: props.staticContext || {},
          regionId,
          token,
          selectedLang,
          userCurrency,
        }),
      );
    }
    if (state.router.action === "PUSH" && __CLIENT__) {
      window.scrollTo(0, 0);
    }

    return null;
  };

export default propertyPageData.reducer;

export const {
  getPropertyPageRequesting,
  getPropertyPageSuccess,
  getPropertyPageError,
  updateNearbyData,
  getGalleryModalData,
  updateBasicDetails,
  getGalleryCarouselData,
  getVideoModalData,
  setReviewMediaGalleryOpen,
  updatePropertyFilters,
  updateRoomTypes,
  setShowShareModalData,
  setShowNextRoomButton,
  setExpandedRoomType,
  updatePropertyPageFilters,
  updatePropertyPageHref,
  updateNearbyProperties,
  updateNearbyLocations,
  updateInstallmentPlans,
  updateBudgetFriendlyProperties,
  updateInventoryReviewData,
  setInstallmentPlansFetching,
  updatePriceTrendsData,
  updatePropertyFilterOptions,
} = propertyPageData.actions;
